import apiClient from "@shared/services/apiClient";

const SETTINGS_API_BASE = "/users/settings";

let cachedGeneralSettings: GeneralSettings | null = null;
let cachedGeneralSettingsPromise: Promise<GeneralSettings> | null = null;

function cloneGeneralSettings(settings: GeneralSettings): GeneralSettings {
  return { ...settings };
}

export interface GeneralSettings {
  settings_id?: string;
  user_id?: string;
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
  theme: string;
  font_size: string;
  color_vision_mode?: string;
  default_view: string;
  high_contrast: boolean;
  reduce_motion: boolean;
  screen_reader: boolean;
}

export interface NotificationPreferences {
  preference_id?: string;
  user_id?: string;
  email_enabled: boolean;
  push_enabled: boolean;
  sms_enabled: boolean;
  patient_updates: boolean;
  study_results: boolean;
  system_alerts: boolean;
  report_ready: boolean;
  mention_replies: boolean;
  clinical_findings: boolean; // REG-004: FDA 21 CFR 820.198 compliance
  security_events: boolean; // REG-004: HIPAA §164.308(a)(6) compliance
  administrative_alerts: boolean; // REG-004: ISO 13485 compliance
  appointment_reminder_hours: number;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  daily_digest: boolean;
  weekly_summary: boolean;
}

export interface NotificationCategoryPreference {
  category: string;
  email_enabled: boolean;
  push_enabled: boolean;
  sms_enabled: boolean;
  in_app_enabled: boolean;
}

export interface NotificationCategoryCatalogEntry {
  category: string;
  group_name: string;
  legacy_type: "alert" | "warning" | "info" | "success";
  default_severity: "critical" | "warning" | "info";
  default_dedup_window_s: number;
  default_escalation_min: number;
  is_report_category: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface PrivacySettings {
  privacy_id?: string;
  user_id?: string;
  profile_visible: boolean;
  show_email: boolean;
  show_phone: boolean;
  analytics_consent: boolean;
  usage_tracking: boolean;
  research_consent: boolean;
  activity_visible: boolean;
  online_status: boolean;
  auto_delete_after: number | null;
}

export interface SecurityPreferences {
  security_id?: string;
  user_id?: string;
  custom_session_timeout: number | null;
  concurrent_sessions: number;
  require_2fa: boolean;
  backup_codes_generated: boolean;
  login_alerts: boolean;
  suspicious_login_block: boolean;
  password_expiry_days: number | null;
  password_history_count: number;
  trusted_devices: any | null;
  ip_whitelist: string[];
}

export interface ApplicationSettings {
  app_settings_id?: string;
  user_id?: string;
  auto_save: boolean;
  auto_save_interval: number;
  confirm_navigation: boolean;
  items_per_page: number;
  default_sort: string;
  default_montage_id: string | null;
  default_sensitivity: number | null;
  default_time_scale: number | null;
  custom_preferences: {
    workspace_density?: "comfortable" | "cozy" | "compact";
    [key: string]: unknown;
  } | null;
}

export const settingsService = {
  // Get all settings
  async getAllSettings() {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      SETTINGS_API_BASE,
    );
    return response.data.data;
  },

  // General Settings
  async getGeneralSettings(): Promise<GeneralSettings> {
    if (cachedGeneralSettings) {
      return cloneGeneralSettings(cachedGeneralSettings);
    }

    if (cachedGeneralSettingsPromise) {
      const settings = await cachedGeneralSettingsPromise;
      return cloneGeneralSettings(settings);
    }

    cachedGeneralSettingsPromise = apiClient
      .get<{
        success: boolean;
        data: GeneralSettings;
      }>(`${SETTINGS_API_BASE}/general`)
      .then((response) => response.data.data)
      .then((settings) => {
        cachedGeneralSettings = settings;
        return settings;
      })
      .finally(() => {
        cachedGeneralSettingsPromise = null;
      });

    const settings = await cachedGeneralSettingsPromise;
    return cloneGeneralSettings(settings);
  },

  async updateGeneralSettings(
    settings: Partial<GeneralSettings>,
  ): Promise<GeneralSettings> {
    const response = await apiClient.put<{
      success: boolean;
      data: GeneralSettings;
    }>(`${SETTINGS_API_BASE}/general`, settings);
    cachedGeneralSettings = response.data.data;
    cachedGeneralSettingsPromise = null;
    return cloneGeneralSettings(response.data.data);
  },

  // Notification Preferences
  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const response = await apiClient.get<{
      success: boolean;
      data: NotificationPreferences;
    }>(`/notifications/preferences/me`);
    return response.data.data;
  },

  async updateNotificationPreferences(
    preferences: Partial<NotificationPreferences>,
  ): Promise<NotificationPreferences> {
    const response = await apiClient.patch<{
      success: boolean;
      data: NotificationPreferences;
    }>(`/notifications/preferences/me`, preferences);
    return response.data.data;
  },

  async sendTestNotification(channel: "email" | "sms" | "push"): Promise<void> {
    await apiClient.post<{
      success: boolean;
      message: string;
    }>(`/notifications/preferences/test`, { channel });
  },

  async getNotificationCategoryPreferences(): Promise<
    NotificationCategoryPreference[]
  > {
    const response = await apiClient.get<{
      data: NotificationCategoryPreference[];
    }>(`/notifications/preferences/categories`);
    return response.data.data || [];
  },

  async updateNotificationCategoryPreferences(
    preferences: NotificationCategoryPreference[],
  ): Promise<NotificationCategoryPreference[]> {
    const response = await apiClient.patch<{
      data: NotificationCategoryPreference[];
    }>(`/notifications/preferences/categories`, { preferences });
    return response.data.data || [];
  },

  async getNotificationCategoryCatalog(): Promise<NotificationCategoryCatalogEntry[]> {
    const response = await apiClient.get<{
      data: NotificationCategoryCatalogEntry[];
    }>(`/notifications/catalog/categories`);
    return response.data.data || [];
  },

  // Privacy Settings
  async getPrivacySettings(): Promise<PrivacySettings> {
    const response = await apiClient.get<{
      success: boolean;
      data: PrivacySettings;
    }>(`${SETTINGS_API_BASE}/privacy`);
    return response.data.data;
  },

  async updatePrivacySettings(
    settings: Partial<PrivacySettings>,
  ): Promise<PrivacySettings> {
    const response = await apiClient.put<{
      success: boolean;
      data: PrivacySettings;
    }>(`${SETTINGS_API_BASE}/privacy`, settings);
    return response.data.data;
  },

  // Security Preferences
  async getSecurityPreferences(): Promise<SecurityPreferences> {
    const response = await apiClient.get<{
      success: boolean;
      data: SecurityPreferences;
    }>(`${SETTINGS_API_BASE}/security`);
    return response.data.data;
  },

  async updateSecurityPreferences(
    preferences: Partial<SecurityPreferences>,
  ): Promise<SecurityPreferences> {
    const response = await apiClient.put<{
      success: boolean;
      data: SecurityPreferences;
    }>(`${SETTINGS_API_BASE}/security`, preferences);
    return response.data.data;
  },

  // Application Settings
  async getApplicationSettings(): Promise<ApplicationSettings> {
    const response = await apiClient.get<{
      success: boolean;
      data: ApplicationSettings;
    }>(`${SETTINGS_API_BASE}/application`);
    return response.data.data;
  },

  async updateApplicationSettings(
    settings: Partial<ApplicationSettings>,
  ): Promise<ApplicationSettings> {
    const response = await apiClient.put<{
      success: boolean;
      data: ApplicationSettings;
    }>(`${SETTINGS_API_BASE}/application`, settings);
    return response.data.data;
  },
};
